import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Avatar, useMediaQuery } from "@mui/material";
import { useLocation } from "wouter";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";

import { useModalA11y } from "../../hooks/useModalA11y";
import { useLogout } from "../../hooks/useLogout";
import { BREAKPOINTS } from "../../utils/constants";
import { getUserInitials } from "../../utils";
import {
  MAIN_NAV_ITEMS,
  MORE_NAV_ITEMS,
  NAVIGATION_ITEMS,
  SETTINGS_NAV,
} from "./constants";
import { STORAGE_KEYS } from "./tokens";
import { DeletePopUp } from "../PopUp";
import { VisuallyHidden } from "../VisuallyHidden";

import {
  AvatarButton,
  AvatarWrapper,
  BottomDrawer,
  BottomDrawerBackdrop,
  BottomDrawerDestructiveButton,
  BottomDrawerDivider,
  BottomDrawerHandle,
  BottomDrawerNavItem,
  BottomDrawerNavItemActive,
  BottomDrawerSection,
  BottomNavContainer,
  BottomNavIcon,
  BottomNavIconPill,
  BottomNavItem,
  BottomNavLabel,
  BottomNavMoreButton,
  BrandMark,
  BrandSection,
  BrandText,
  DropdownItem,
  DropdownItemDestructive,
  DropdownMenu,
  NavbarContainer,
  NavbarInner,
  NavList,
  NavLinkItem,
} from "./styles";
import { TopNavbarProps } from "./types";

const MOBILE_BREAKPOINT = BREAKPOINTS.tabletLarge - 1;

export const TopNavbar: React.FC<TopNavbarProps> = ({
  user,
  endSession,
  userAvatar,
}) => {
  const [location, setLocation] = useLocation();
  const isMobile = useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT}px)`);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const { performLogout, isLoggingOut } = useLogout(endSession);

  const drawerRef = useRef<HTMLDivElement>(null);
  useModalA11y(isMobile && drawerOpen, drawerRef);

  const avatarRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const userInitials = getUserInitials(user?.name);

  const closeAllMenus = useCallback(() => {
    setDropdownOpen(false);
    setDrawerOpen(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        avatarRef.current &&
        !avatarRef.current.contains(target)
      ) {
        setDropdownOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeAllMenus();
        if (dropdownRef.current) avatarRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleLogoutClick = useCallback(() => {
    closeAllMenus();
    if (localStorage.getItem(STORAGE_KEYS.skipLogoutConfirm) !== "true") {
      setShowLogoutConfirm(true);
    } else {
      performLogout();
    }
  }, [closeAllMenus, performLogout]);

  const handleSettingsClick = useCallback(() => {
    closeAllMenus();
    setLocation("/settings");
  }, [closeAllMenus, setLocation]);

  const isActive = (to: string, exact: boolean) => {
    if (exact) return location === to;
    return location.startsWith(to);
  };

  const isMoreActive = useMemo(
    () =>
      MORE_NAV_ITEMS.some((item) =>
        item.exact ? location === item.to : location.startsWith(item.to),
      ) ||
      (SETTINGS_NAV.exact
        ? location === SETTINGS_NAV.to
        : location.startsWith(SETTINGS_NAV.to)),
    [location],
  );

  const handleDrawerNavClick = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  return (
    <>
      <DeletePopUp
        isOpen={showLogoutConfirm}
        setIsOpen={setShowLogoutConfirm}
        title="Confirm Logout"
        text="Are you sure you want to log out? You'll need to sign in again to access your account."
        handleOnDelete={performLogout}
        confirmLabel="Logout"
        loadingLabel="Logging out..."
        showDontAskAgain={true}
        dontAskAgainKey={STORAGE_KEYS.skipLogoutConfirm}
        isLoading={isLoggingOut}
      />

      {/* Desktop / Tablet top navbar */}
      {!isMobile && (
        <NavbarContainer aria-label="Main navigation">
          <NavbarInner>
            <BrandSection to="/">
              <BrandMark aria-hidden="true">N</BrandMark>
              <BrandText>Net Worth Tracker</BrandText>
              <VisuallyHidden as="span"> home</VisuallyHidden>
            </BrandSection>

            <NavList>
              {NAVIGATION_ITEMS.map((item) => (
                <li key={item.name}>
                  <NavLinkItem
                    to={item.to}
                    $active={isActive(item.to, item.exact)}
                    aria-current={
                      isActive(item.to, item.exact) ? "page" : undefined
                    }
                    data-testid={item.testId}
                  >
                    {item.name}
                  </NavLinkItem>
                </li>
              ))}
            </NavList>

            <AvatarWrapper>
              <AvatarButton
                ref={avatarRef}
                onClick={() => setDropdownOpen((prev) => !prev)}
                aria-label="User menu"
                aria-expanded={dropdownOpen}
                aria-haspopup="menu"
              >
                <Avatar
                  src={userAvatar}
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                    fontSize: "var(--text-sm)",
                    fontWeight: 600,
                  }}
                >
                  {userInitials}
                </Avatar>
              </AvatarButton>

              {dropdownOpen && (
                <DropdownMenu
                  ref={dropdownRef}
                  role="menu"
                  aria-label="User menu"
                >
                  <DropdownItem role="menuitem" onClick={handleSettingsClick}>
                    <SettingsRoundedIcon fontSize="small" />
                    Settings
                  </DropdownItem>
                  <DropdownItemDestructive
                    role="menuitem"
                    onClick={handleLogoutClick}
                    disabled={isLoggingOut}
                  >
                    <LogoutRoundedIcon fontSize="small" />
                    {isLoggingOut ? "Logging out..." : "Logout"}
                  </DropdownItemDestructive>
                </DropdownMenu>
              )}
            </AvatarWrapper>
          </NavbarInner>
        </NavbarContainer>
      )}

      {/* Mobile bottom nav */}
      {isMobile && (
        <>
          <BottomNavContainer aria-label="Main navigation">
            {MAIN_NAV_ITEMS.map((item) => {
              const active = isActive(item.to, item.exact);
              return (
                <BottomNavItem
                  key={item.name}
                  to={item.to}
                  $active={active}
                  aria-current={active ? "page" : undefined}
                  data-testid={item.testId}
                >
                  <BottomNavIconPill $active={active}>
                    <BottomNavIcon aria-hidden="true">
                      {item.icon}
                    </BottomNavIcon>
                  </BottomNavIconPill>
                  <BottomNavLabel>{item.name}</BottomNavLabel>
                </BottomNavItem>
              );
            })}

            <BottomNavMoreButton
              $active={isMoreActive || drawerOpen}
              onClick={() => setDrawerOpen((prev) => !prev)}
              aria-label="More options"
              aria-expanded={drawerOpen}
              aria-haspopup="dialog"
            >
              <BottomNavIconPill $active={isMoreActive || drawerOpen}>
                <BottomNavIcon aria-hidden="true">
                  <MoreHorizRoundedIcon fontSize="small" />
                </BottomNavIcon>
              </BottomNavIconPill>
              <BottomNavLabel>More</BottomNavLabel>
            </BottomNavMoreButton>
          </BottomNavContainer>

          {/* More drawer */}
          <BottomDrawerBackdrop
            $open={drawerOpen}
            onClick={handleDrawerNavClick}
            aria-hidden="true"
          />
          <BottomDrawer
            ref={drawerRef}
            $open={drawerOpen}
            tabIndex={-1}
            role="dialog"
            aria-label="More options"
            aria-modal="true"
          >
            <BottomDrawerHandle aria-hidden="true" />

            <BottomDrawerSection>
              {MORE_NAV_ITEMS.map((item) => {
                const active = isActive(item.to, item.exact);
                const ItemComponent = active
                  ? BottomDrawerNavItemActive
                  : BottomDrawerNavItem;
                return (
                  <ItemComponent
                    key={item.name}
                    to={item.to}
                    data-testid={item.testId}
                    aria-current={active ? "page" : undefined}
                    onClick={handleDrawerNavClick}
                  >
                    {item.icon}
                    {item.name}
                  </ItemComponent>
                );
              })}
            </BottomDrawerSection>

            <BottomDrawerDivider />

            <BottomDrawerSection>
              {(() => {
                const active = isActive(SETTINGS_NAV.to, SETTINGS_NAV.exact);
                const ItemComponent = active
                  ? BottomDrawerNavItemActive
                  : BottomDrawerNavItem;
                return (
                  <ItemComponent
                    to={SETTINGS_NAV.to}
                    data-testid={SETTINGS_NAV.testId}
                    aria-current={active ? "page" : undefined}
                    onClick={handleDrawerNavClick}
                  >
                    <SettingsRoundedIcon fontSize="small" />
                    Settings
                  </ItemComponent>
                );
              })()}
              <BottomDrawerDestructiveButton
                onClick={handleLogoutClick}
                disabled={isLoggingOut}
              >
                <LogoutRoundedIcon fontSize="small" />
                {isLoggingOut ? "Logging out..." : "Logout"}
              </BottomDrawerDestructiveButton>
            </BottomDrawerSection>
          </BottomDrawer>
        </>
      )}
    </>
  );
};
